import React, {useContext} from "react";
import {Template, TemplateWithoutBody} from "../../model/Template";
import {Customer} from "../../model/Customer";
import {Link, useNavigate, useParams} from "react-router-dom";
import {Box, Button} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useCopyTemplateDialog} from "../../hooks/useCopyTemplateDialog";
import {useLearningLinkDialog} from "../../hooks/useLearningLinkDialog";
import TemplateTable from "./TemplateTable";
import {HeaderCard} from "../Base/HeaderCard";
import {NavigationHeader} from "../Navigation/NavigationHeader";
import {UserContext} from "../../App";

interface Props {
    customers: Customer[];
    templates: TemplateWithoutBody[];
}

export const AdminTemplateOverview: React.FC<Props> = ({customers, templates}) => {
    const {t} = useTranslation();
    const {user} = useContext(UserContext);
    const {customerId} = useParams();
    const navigate = useNavigate();

    const [selectedTemplateIds, setSelectedTemplateIds] = React.useState<string[]>([]);
    const handleSelectionChange = (newSelection: string[]) => setSelectedTemplateIds(newSelection);

    const [copyTemplateDialog, openCopyTemplateDialog] = useCopyTemplateDialog();
    const handleOpenCopyTemplateDialog = () => {
        openCopyTemplateDialog(handleCopyTemplateResult, customerId!, selectedTemplateIds, customers);
    }
    const handleCopyTemplateResult = (data: Template[], targetCustomerId: string) => {
        if (customerId?.toString().localeCompare(targetCustomerId) === 0) {
            navigate('.', {replace: true});
        }
    }

    const [learningLinkDialog, languageDialog, openLearningLinkDialog, learningLinksLoading] = useLearningLinkDialog();
    const handleOpenLearningLinkDialog = () => openLearningLinkDialog(selectedTemplateIds);

    const navigation = [
        {url: "", label: t('templateManager.title')}
    ];

    return (
        <>
            <NavigationHeader path={navigation}/>
            <HeaderCard
                headline={<>{t('templateManager.overviewHeader', {name: user?.firstName ?? ""})}</>}
                body={<>{t('templateManager.overviewDescription')}</>}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 2
                }}
            >
                <Link to={"new"}>
                    <Button variant="contained" color="look2xBlue">
                        {t('templateManager.newTemplate')}
                    </Button>
                </Link>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    <Button
                        disabled={selectedTemplateIds.length === 0 || learningLinksLoading}
                        onClick={handleOpenLearningLinkDialog}
                        variant="contained"
                        color="look2xBlue"
                    >
                        {t('templateTable.generateLearningLinks')}
                    </Button>
                    {learningLinkDialog}
                    {languageDialog}
                    <Button
                        disabled={selectedTemplateIds.length === 0 || learningLinksLoading}
                        onClick={handleOpenCopyTemplateDialog}
                        variant="contained"
                        color="look2xBlue"
                    >
                        {t('templateTable.copyTemplates')}
                    </Button>
                    {copyTemplateDialog}
                </Box>
            </Box>
                    <TemplateTable
                        templates={templates}
                        selectedTemplateIds={selectedTemplateIds}
                        onSelectionChange={handleSelectionChange}
                        readonly={false}
                        columnVisibility={{
                            difficulty: true,
                            loginPage: true,
                            campaigns: true,
                            testmail: true,
                            edit: true,
                            preview: true
                        }}
                        pageSize={15}
                    />
        </>
    );
}

